import { api } from "./client";
import type { Site, SiteCreate, SiteUpdate } from "@/types";
import type { Desenho } from "@/lib/plantaDesenho";

export const sitesApi = {
  async list(params?: { tenant_id?: string }): Promise<Site[]> {
    const { data } = await api.get<Site[]>("/sites", { params });
    return data;
  },
  async get(id: string): Promise<Site> {
    const { data } = await api.get<Site>(`/sites/${id}`);
    return data;
  },
  async create(payload: SiteCreate): Promise<Site> {
    const { data } = await api.post<Site>("/sites", payload);
    return data;
  },
  async update(id: string, payload: SiteUpdate): Promise<Site> {
    const { data } = await api.patch<Site>(`/sites/${id}`, payload);
    return data;
  },
  async remove(id: string): Promise<void> {
    await api.delete(`/sites/${id}`);
  },

  /**
   * Baixa a imagem da planta e devolve uma `data:` URL pronta pro `<img>`.
   *
   * Não dá pra apontar um `<img src>` direto pra este endpoint: a rota exige
   * autenticação (a planta mostra o layout físico da operação do cliente), e
   * o token vive em localStorage, anexado pelo interceptor do axios. Uma
   * requisição de `<img>` é feita pelo NAVEGADOR — vai sem header nenhum,
   * volta 401, e o que aparece é o ícone de imagem quebrada. O logo do
   * tenant, servido por rota parecida e funcionando com `<img>`, é a
   * armadilha: aquela rota é deliberadamente pública. Esta não é.
   *
   * POR QUE `data:` E NÃO `URL.createObjectURL`, que é o caminho óbvio:
   *
   * object URL tem TEMPO DE VIDA. Ele mora num mapa do documento até alguém
   * chamar `revokeObjectURL`, e quem chama é um cleanup de efeito — que
   * dispara em toda troca de dependência, remontagem e desmontagem. Se o
   * revoke acontecer enquanto um `<img>` ainda aponta pra ele, o navegador
   * recusa a URL e a tela mostra imagem quebrada com a requisição HTTP tendo
   * dado certo. Foi isso que apareceu em produção: no DevTools, a chamada à
   * API em verde e a `blob:` logo abaixo em vermelho.
   *
   * A `data:` URL não tem dono nem ciclo de vida — é uma string. Vive e morre
   * com a entrada do cache do react-query, e não há race possível. Custa ~33%
   * de memória a mais que o binário, o que numa planta de até 8 MB é um preço
   * honesto por não ter essa classe de bug. É o mesmo caminho que o worker do
   * relatório já usa pra embutir a planta no PDF.
   */
  async fetchPlanta(id: string): Promise<string> {
    const { data } = await api.get<Blob>(`/sites/${id}/planta`, {
      responseType: "blob",
    });
    return await new Promise<string>((resolve, reject) => {
      const leitor = new FileReader();
      leitor.onload = () => resolve(String(leitor.result));
      leitor.onerror = () => reject(leitor.error ?? new Error("leitura falhou"));
      leitor.readAsDataURL(data);
    });
  },

  async uploadPlanta(id: string, file: File): Promise<Site> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post<Site>(`/sites/${id}/planta`, form);
    return data;
  },

  async removePlanta(id: string): Promise<void> {
    await api.delete(`/sites/${id}/planta`);
  },

  // --- Desenho vetorial (v41) ---------------------------------------------
  //
  // Rotas próprias, fora do PATCH do site: o editor grava o documento
  // inteiro, e pelo payload geral duas abas abertas transformariam um traço
  // de parede em sobrescrita de cadastro.

  async lerDesenho(id: string): Promise<Desenho | null> {
    const { data } = await api.get<Desenho | null>(`/sites/${id}/desenho`);
    return data ?? null;
  },

  async gravarDesenho(id: string, desenho: Desenho): Promise<Site> {
    const { data } = await api.put<Site>(`/sites/${id}/desenho`, desenho);
    return data;
  },

  async apagarDesenho(id: string): Promise<void> {
    await api.delete(`/sites/${id}/desenho`);
  },
};
